import React, { useState } from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  InputBase,
  Box,
  IconButton,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  alpha,
} from "@mui/material";
import {
  Search as SearchIcon,
  AccountCircle,
  Logout,
  Settings,
  Person,
} from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
// import { useAuth } from './contexts/AuthContext';
import { useNavigate } from "react-router-dom";
import tcris3Logo from "./images/MedicaLife_sigla.png";
import LanguageSelector from "./components/LanguageSelector";
import { clearAuthStorage } from "./services/authFailureRedirect";

// Styled components for search functionality
const SearchContainer = styled("div")(({ theme }) => ({
  position: "relative",
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.white, 0.15),
  "&:hover": {
    backgroundColor: alpha(theme.palette.common.white, 0.25),
  },
  marginLeft: 0,
  width: "100%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(1),
    width: "auto",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: {
      width: "12ch",
      "&:focus": {
        width: "20ch",
      },
    },
  },
}));

interface HeaderProps {
  onSearch?: (searchTerm: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ onSearch }) => {
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { t } = useTranslation();
  //const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();

  const readStoredUser = () => {
    const raw = localStorage.getItem("user");
    if (!raw) return null;
    try {
      return JSON.parse(raw) as {
        name?: string;
        displayName?: string;
        username?: string;
        email?: string;
      };
    } catch {
      return null;
    }
  };

  const [storedUser, setStoredUser] = useState<
    ReturnType<typeof readStoredUser>
  >(() => readStoredUser());

  const userNameToShow =
    storedUser?.name || storedUser?.displayName || storedUser?.username || "";

  const userEmailToShow = storedUser?.email || "";

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    console.log("[Header] Search term changed:", value);

    // Call the onSearch callback if provided
    if (onSearch) {
      onSearch(value);
    }
  };

  const handleSearchSubmit = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      console.log("[Header] Search submitted:", searchTerm);
      // Handle search submission here
      if (onSearch) {
        onSearch(searchTerm);
      }
    }
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    // Refresh from storage in case login state changed
    setStoredUser(readStoredUser());
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    console.log("[Header] Profile clicked");
    handleMenuClose();
    navigate("/profile");
  };

  const handleSettings = () => {
    console.log("[Header] Settings clicked");
    handleMenuClose();
    navigate("/settings");
  };

  const handleLogout = async () => {
    console.log("[Header] Logout clicked");
    handleMenuClose();
    //await logout();
    clearAuthStorage();
    navigate("/login", { replace: true });
  };

  const handleLogin = () => {
    console.log("[Header] Login clicked");
    navigate("/login");
  };

  const handleLogoClick = () => {
    console.log("[Header] Logo clicked");
    navigate("/dashboard");
  };

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "primary.main",
      }}
    >
      <Toolbar>
        {/* Logo and Application Name */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            cursor: "pointer",
            mr: 2,
          }}
          onClick={handleLogoClick}
        >
          <Avatar
            sx={{
              width: 100,
              height: 50,
              bgcolor: "secondary.main",
              mr: 1,
              fontSize: "1.2rem",
              fontWeight: "bold",
              borderRadius: 1, // Makes it rectangular instead of circular
            }}
            src={tcris3Logo} // Using imported tcris3.png logo
            alt="TCRIS Logo"
          >
            TC
          </Avatar>
          <Typography
            variant="h6"
            component="div"
            sx={{
              fontWeight: "bold",
              color: "white",
              display: { xs: "none", sm: "block" },
            }}
          >
            {t("header.appName")}
          </Typography>
        </Box>

        {/* Search Field */}
        <SearchContainer>
          <SearchIconWrapper>
            <SearchIcon />
          </SearchIconWrapper>
          <StyledInputBase
            placeholder={t("header.searchPlaceholder")}
            inputProps={{ "aria-label": "search" }}
            value={searchTerm}
            onChange={handleSearchChange}
            onKeyDown={handleSearchSubmit}
          />
        </SearchContainer>

        {/* Spacer to push user menu to the right */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Language Selector */}
        <LanguageSelector />

        {/* User Menu */}
        {storedUser ? (
          <>
            <Box sx={{ display: "flex", alignItems: "center", mr: 1 }}>
              <Typography
                variant="body2"
                sx={{
                  color: "white",
                  mr: 1,
                  display: { xs: "none", md: "block" },
                }}
              >
                {t("header.greeting") + userNameToShow}
              </Typography>
            </Box>
            <IconButton
              size="large"
              edge="end"
              aria-label="account of current user"
              aria-controls="primary-search-account-menu"
              aria-haspopup="true"
              onClick={handleMenuOpen}
              color="inherit"
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: "secondary.main",
                  fontSize: "0.9rem",
                }}
              >
                {(userNameToShow || "U").slice(0, 1).toUpperCase()}
              </Avatar>
            </IconButton>
          </>
        ) : (
          <IconButton
            size="large"
            edge="end"
            color="inherit"
            onClick={handleLogin}
            aria-label="login"
          >
            <AccountCircle />
          </IconButton>
        )}

        {/* User Menu Dropdown */}
        <Menu
          anchorEl={anchorEl}
          id="primary-search-account-menu"
          keepMounted
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
        >
          {/* {user && ( */}
          {storedUser && (
            <>
              <MenuItem disabled>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    {userNameToShow}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {userEmailToShow}
                  </Typography>

                  {false && (
                    <Typography variant="caption" color="text.secondary">
                      {/* {user.department} */}
                      Departament
                    </Typography>
                  )}
                </Box>
              </MenuItem>
              <Divider />
            </>
          )}
          <MenuItem onClick={handleProfile}>
            <Person sx={{ mr: 1 }} />
            {t("header.profile")}
          </MenuItem>
          <MenuItem onClick={handleSettings}>
            <Settings sx={{ mr: 1 }} />
            {t("header.settings")}
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleLogout}>
            <Logout sx={{ mr: 1 }} />
            {t("header.logout")}
          </MenuItem>
        </Menu>
      </Toolbar>
    </AppBar>
  );
};

export default Header;
